import { useEffect, useMemo } from 'react'
import ConfigurationSidebar from './configuration-sidebar.component'
import { useSession } from 'src/providers/auth.provider'
import { useCurrentProject, WithPermission } from 'src/providers/current-project.provider'
import { Outlet } from 'react-router-dom'
import { useLocalizedNavigate } from 'src/hooks/use-localized-navigate'
import { useTranslation } from 'react-i18next'
import { ProjectService } from 'src/services/project.service'
import { useQuery } from '@tanstack/react-query'
import {
  buildVariableStore,
  getContentVariables,
  getDefaultContentVariable,
} from '../followups/[followupId]/messages/_components/variable-store.helper'
import { ErrorQueryHandler } from 'src/components/error/error-query-handler.component'
import { TContent } from '../contents/_components/content.type'
import { VariablesContext } from './variables-context'
import {
  EPermissionGrantActions,
  EPermissionGrantSubjects,
} from '../permissions/_components/permissions.type'

export default function ConfigurationLayout() {
  const { t } = useTranslation()
  const { currentUser } = useSession()
  const { project, isFeatureActivated } = useCurrentProject()
  const navigate = useLocalizedNavigate()

  const {
    isLoading,
    error,
    data: contents,
  } = useQuery({
    queryKey: ['contents', project.id],
    queryFn: () => ProjectService.getSelfConfigurationContents(project.id),
  })

  const value = useMemo(() => {
    return {
      variableStore: buildVariableStore(t),
      contentVariables: getContentVariables(t, contents as TContent[]),
      defaultContentVariable: getDefaultContentVariable(t),
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contents])

  useEffect(() => {
    if (!isFeatureActivated('is-configuration-activated')) {
      navigate(`/${project.id}/medical`, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, project.id, navigate])

  if (error) return <ErrorQueryHandler error={error} />
  if (isLoading) return <div className="loading" />

  return (
    <>
      <ConfigurationSidebar />
      <div className="c-flex c-w-full c-min-w-0">
        <WithPermission
          requiredSubject={EPermissionGrantSubjects.CONFIGURATION}
          requiredAction={EPermissionGrantActions.GET}
          onFailure={'permission-error-component'}
        >
          <VariablesContext.Provider value={value}>
            <Outlet />
          </VariablesContext.Provider>
        </WithPermission>
      </div>
    </>
  )
}
